import {getParameterConfigsSpecFromReadShape} from 'insights/analytics-dashboards/RESTAnalyticsDashboardTypes';
import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import {
  AnalyticsParameterConfigSpecRequest,
  RESTAnalyticsParameterValue,
} from 'insights/parameters/RESTParameterTypes';
import {ExportQueryData, RunQueryData} from 'insights/warehouse/AnalyticsWarehouseDAO';
import {ExportQueryOutputSettings} from 'insights/warehouse/AnalyticsWarehouseTypes';

import {AnalyticsParameterConfigGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import {restGetQueryParameterConfigs} from './getQueryParameterConfigs';
import {convertGQLToRESTParameterValue} from './ParameterGQLRESTConverters';
import {convertRESTToWarehouseParameters} from './ParameterRESTConverters';

export function restGetRunQueryData({
  parameterConfigs,
  parameterValues,
  sqlQuery,
}: {
  parameterConfigs: Array<AnalyticsParameterConfigSpecRequest>;
  parameterValues: Array<RESTAnalyticsParameterValue>;
  sqlQuery: string;
}): RunQueryData {
  return {
    parameterizedSQLQuery: sqlQuery,
    // Only send the parameters that are in the sqlQuery. Filter out unused parameters, which could have
    // invalid values, so they don't affect running this sqlQuery.
    parameters: restGetQueryParameterConfigs(
      sqlQuery,
      convertRESTToWarehouseParameters(parameterConfigs, parameterValues)
    ),
  };
}

/**
 * Get data to run the query
 *
 * Convert SQL query and parameters to RunQueryData. Use this function when sqlQuery, parameterConfigs, and
 * parameterValues are already in sync, and the caller needs to convert to REST warehouse parameters to run
 * the query.
 */
export function getRunQueryData({
  parameterConfigs,
  parameterValues,
  sqlQuery,
}: {
  parameterConfigs: Array<AnalyticsParameterConfigGraphQL>;
  parameterValues: Array<AnalyticsParameterValue>;
  sqlQuery: string;
}): RunQueryData {
  return restGetRunQueryData({
    parameterConfigs: getParameterConfigsSpecFromReadShape(parameterConfigs),
    parameterValues: parameterValues.map((value) => convertGQLToRESTParameterValue(value)),
    sqlQuery,
  });
}

export function getExportQueryData({
  parameterConfigs,
  parameterValues,
  sqlQuery,
  ...outputSettings
}: {
  parameterConfigs: Array<AnalyticsParameterConfigGraphQL>;
  parameterValues: Array<AnalyticsParameterValue>;
  sqlQuery: string;
} & ExportQueryOutputSettings): ExportQueryData {
  return {
    ...restGetRunQueryData({
      parameterConfigs: getParameterConfigsSpecFromReadShape(parameterConfigs),
      parameterValues: parameterValues.map((value) => convertGQLToRESTParameterValue(value)),
      sqlQuery,
    }),
    ...outputSettings,
  };
}
